// Subscribe Allocate form widget — state-aware (Phase 6A).
//
// Variant lookup driven by the subscription's dossier state + the
// latest event from the timeline rollup. Replaces the Phase 2.4b mock
// state machine.
//
//   currentPhase           latestEventType          Variant
//   ─────────────────────  ──────────────────────  ──────────
//   03-kyc-verified        (none / kyc.verified)   idle
//   03-kyc-verified        allocation.requested    pending
//   04-allocated           *                       attested
//   ≥ 05-*                 *                       historic
//
// On submit, POSTs allocation.requested (Path B, non-lifecycle) and
// flips to pending after the refetch sees the new event. Path B
// events do NOT update state.current.yaml's last_event_id, which is
// why we read the timeline directly via useRecentEvents (the
// state.lastEvent path keeps showing the prior lifecycle event
// indefinitely for non-lifecycle entries).

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Money } from '@rmint/ui';
import { useSubscriptionState } from '../../lib/use-subscription-state';
import { useOffering, type Offering } from '../../lib/offering';
import { useRecentEvents } from '../../lib/recent-events';
import type { WidgetProps } from './registry';

// Phase 6A officer routing: founder is sole officer, projector's Phase 3a
// fallback resolves {officerId} to 'of_olivia' when vars.officerId is
// absent. We pass it explicitly so the projection target stays
// predictable across calls + matches the runbook's curl backdoor (which
// uses the same officer ID for the attestation file). Phase 9 admin
// canvas + multi-officer routing supersede this.
const PHASE_6_OFFICER_ID = 'of_olivia';

// Fallback offering used when the dossier read fails or the
// restaurant directory has no offering.yaml. Sakura is the only
// live offering in Phase 8/6A; the fallback prevents the form from
// blocking on a missing-or-late YAML read in dev / e2e harnesses.
const FALLBACK_OFFERING: Offering = {
  rmintId: 'sakura-ramen-test-d1',
  restaurantName: 'Sakura Ramen NC',
  ticker: 'RMOT-SAKURA-D1',
  tokenContractAddress: '0xB03e42d3B2F86343dEcC3631bBd792F7f988eCAd',
  saleContractAddress: '0xf8FF966494E91283Ca7b744c60F12811C6bD92dC',
  pricePerToken: 1.0,
  minAmountUsdc: 1000,
  maxAmountUsdc: 50000,
  lockupMonths: 36,
  lockupEndIso: '2029-05-09T00:00:00Z',
  signByDeadlineIso: '2026-06-09T00:00:00Z',
  estimatedFirstDistributionIso: '2026-08-15T00:00:00Z',
  networkFeeUsdcEst: 2.5,
};

const DEFAULT_AMOUNT_USDC = 5000;

type Variant = 'idle' | 'pending' | 'attested' | 'historic';

// Phases past 03-kyc-verified that this widget treats as "allocation
// already complete." The first three render `attested`; later phases
// render `historic` so a stale chat surface stays informative without
// suggesting another action.
const ATTESTED_PHASES = new Set(['04-allocated']);
const HISTORIC_PHASES = new Set([
  '05-signed',
  '06-purchased',
  '07-holding',
  '08-settled',
]);

// Exported for unit testing — pure function, no React.
export function selectVariant(
  currentPhase: string | undefined,
  lastEventType: string | undefined,
): Variant {
  if (!currentPhase) return 'idle';
  if (ATTESTED_PHASES.has(currentPhase)) return 'attested';
  if (HISTORIC_PHASES.has(currentPhase)) return 'historic';
  if (currentPhase === '03-kyc-verified' && lastEventType === 'allocation.requested') {
    return 'pending';
  }
  return 'idle';
}

export function SubscribeAllocateForm(props: WidgetProps) {
  const navigate = useNavigate();
  const subscriptionId =
    (props.subscriptionId as string | undefined) ??
    (props.subscriptionid as string | undefined) ??
    '';

  // Allow /debug/widgets to override the dossier read for visual QA.
  const debugStateOverride = props.state as string | undefined;

  // refetchKey bumps after a successful POST to trigger a fresh state fetch.
  const [refetchKey, setRefetchKey] = useState(0);

  const sub = useSubscriptionState(subscriptionId, refetchKey);
  // Path B detection: read the latest event from the timeline rollup
  // because state.current.yaml's last_event_id only updates for
  // lifecycle-advancing events (per validate-event.ts). Without this,
  // a successful allocation.requested POST would never flip the
  // variant — state.lastEvent.eventType would keep showing kyc.verified.
  const recentEntityRoot = subscriptionId
    ? `subscriptions/${subscriptionId}`
    : undefined;
  const recent = useRecentEvents(recentEntityRoot, 1, refetchKey);
  const latestEventType =
    recent.events[0]?.eventType ?? sub.state?.lastEvent?.eventType;
  const dossierVariant = selectVariant(sub.state?.currentPhase, latestEventType);
  const overrideVariant: Variant | null =
    debugStateOverride === 'pending'
      ? 'pending'
      : debugStateOverride === 'attested'
        ? 'attested'
        : debugStateOverride === 'historic'
          ? 'historic'
          : null;
  const variant: Variant = overrideVariant ?? dossierVariant;

  // Offering lookup — preferred source is state.current.yaml's data.rmintId;
  // fall back to widget prop, then to subscribe-mocks for resilience.
  const dossierRmintId = (sub.state?.raw.data as { rmintId?: string } | undefined)
    ?.rmintId;
  const propRmintId = props.rmintId as string | undefined;
  const rmintId = dossierRmintId ?? propRmintId;
  const off = useOffering(rmintId);
  const offering: Offering = off.offering ?? FALLBACK_OFFERING;

  const [amount, setAmount] = useState<number>(DEFAULT_AMOUNT_USDC);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const tokens = Math.floor(amount / offering.pricePerToken);
  const clampedAmount = Math.max(
    offering.minAmountUsdc,
    Math.min(amount, offering.maxAmountUsdc),
  );

  async function handleSubmit() {
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Source investorId from dossier (best) or fall back to a sentinel
      // the projector will reject (helps surface bad envelopes early).
      const investorId =
        (sub.state?.raw.data as { investorId?: string } | undefined)?.investorId ??
        'inv_unknown';
      const res = await fetch('/events', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          entityType: 'subscription',
          entityId: subscriptionId,
          eventId: `evt_alloc_req_${crypto.randomUUID()}`,
          eventType: 'allocation.requested',
          occurredAt: new Date().toISOString(),
          // I1: explicit officerId so the projection target stays
          // predictable + matches the runbook's curl-backdoor target.
          vars: { officerId: PHASE_6_OFFICER_ID },
          body: {
            subject: { investorId, rmintId: rmintId ?? '' },
            data: {
              subscriptionId,
              amountUsdc: clampedAmount,
              tokens,
              requestedAt: new Date().toISOString(),
            },
          },
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: { kind?: string };
        };
        throw new Error(
          `allocation request failed (${res.status}): ${body.error?.kind ?? 'unknown'}`,
        );
      }
      setRefetchKey((k) => k + 1);
      setSubmitting(false);
    } catch (e) {
      setSubmitError((e as Error).message);
      setSubmitting(false);
    }
  }

  // ─── Render branches ──────────────────────────────────────────────

  if (variant === 'pending') {
    return (
      <SubmittedSummary
        amount={clampedAmount}
        tokens={tokens}
        ticker={offering.ticker}
      />
    );
  }

  if (variant === 'attested') {
    return (
      <AttestedSummary
        amount={clampedAmount}
        tokens={tokens}
        ticker={offering.ticker}
        signByIso={offering.signByDeadlineIso}
        onContinue={() =>
          navigate(`/subscribe/${encodeURIComponent(subscriptionId)}/sign`)
        }
      />
    );
  }

  if (variant === 'historic') {
    return (
      <HistoricSummary
        amount={clampedAmount}
        tokens={tokens}
        ticker={offering.ticker}
        onContinue={() =>
          navigate(`/subscribe/${encodeURIComponent(subscriptionId)}/sign`)
        }
      />
    );
  }

  // idle — the form
  const ctaDisabled = submitting || !subscriptionId || sub.loading;
  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        if (!ctaDisabled) handleSubmit();
      }}
    >
      <div>
        <label
          htmlFor="allocate-amount"
          className="block text-[11px] uppercase tracking-wide text-foreground-mute mb-2"
        >
          How much would you like to invest?
        </label>
        <div className="flex items-baseline gap-3 mb-2">
          <span className="font-mono text-sm text-foreground-mute">$</span>
          <input
            id="allocate-amount"
            type="number"
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value) || 0)}
            min={offering.minAmountUsdc}
            max={offering.maxAmountUsdc}
            step={500}
            className="font-mono text-4xl font-semibold border-b-2 border-primary w-48 px-1 py-1 outline-none bg-transparent text-ink"
          />
          <span className="font-mono text-sm text-foreground-mute">USDC</span>
        </div>
        <input
          type="range"
          min={offering.minAmountUsdc}
          max={offering.maxAmountUsdc}
          step={500}
          value={clampedAmount}
          onChange={(e) => setAmount(Number(e.target.value))}
          className="w-full accent-primary"
          aria-label="Amount slider"
        />
        <div className="flex justify-between font-mono text-[11px] text-foreground-mute mt-1">
          <span>min: <Money value={offering.minAmountUsdc} /></span>
          <span>max: <Money value={offering.maxAmountUsdc} /></span>
        </div>
      </div>

      <dl className="rounded-card border border-border bg-card-warm p-4 grid grid-cols-[1fr_auto] gap-y-2 text-[13px]">
        <dt className="text-foreground-mute">You'll receive</dt>
        <dd className="font-mono text-right font-bold m-0">
          <output htmlFor="allocate-amount">
            {tokens.toLocaleString()} {offering.ticker}
          </output>
        </dd>
        <dt className="text-foreground-mute">Price per token</dt>
        <dd className="font-mono text-right m-0"><Money value={offering.pricePerToken} precision={4} /></dd>
        <dt className="text-foreground-mute">Lockup</dt>
        <dd className="text-right m-0">{offering.lockupMonths} months · through {formatMonthYear(offering.lockupEndIso)}</dd>
        <dt className="text-foreground-mute">First distribution (est.)</dt>
        <dd className="text-right m-0">{formatMonthYear(offering.estimatedFirstDistributionIso)}</dd>
      </dl>

      {submitError ? (
        <div className="rounded-card border border-warn-line bg-warn-bg px-4 py-3 text-[13px] text-warn">
          {submitError}
        </div>
      ) : null}

      <div className="flex justify-end">
        <Button
          variant="primary"
          size="lg"
          type="submit"
          disabled={ctaDisabled}
        >
          {submitting ? 'Submitting…' : 'Submit allocation request →'}
        </Button>
      </div>
    </form>
  );
}

function SubmittedSummary({
  amount,
  tokens,
  ticker,
}: {
  amount: number;
  tokens: number;
  ticker: string;
}) {
  return (
    <div className="space-y-3">
      <div className="rounded-card border border-border bg-card-warm p-4 grid grid-cols-[1fr_auto] gap-y-2 text-[13px]">
        <span className="text-foreground-mute">Submitted</span>
        <span className="font-mono text-right">just now</span>
        <span className="text-foreground-mute">Amount</span>
        <span className="font-mono text-right"><Money value={amount} /></span>
        <span className="text-foreground-mute">Tokens</span>
        <span className="font-mono text-right">{tokens.toLocaleString()} {ticker}</span>
      </div>
      <p className="text-[12px] text-foreground-mute">
        Awaiting compliance officer review.
      </p>
    </div>
  );
}

function AttestedSummary({
  amount,
  tokens,
  ticker,
  signByIso,
  onContinue,
}: {
  amount: number;
  tokens: number;
  ticker: string;
  signByIso: string;
  onContinue: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-card border border-border bg-card-warm p-4 grid grid-cols-[1fr_auto] gap-y-2 text-[13px]">
        <span className="text-foreground-mute">Amount</span>
        <span className="font-mono text-right"><Money value={amount} /></span>
        <span className="text-foreground-mute">Tokens</span>
        <span className="font-mono text-right">{tokens.toLocaleString()} {ticker}</span>
        <span className="text-foreground-mute">Sign by</span>
        <span className="text-right font-semibold">{formatMonthDay(signByIso)}</span>
      </div>
      <div className="flex justify-end">
        <Button variant="primary" size="lg" onClick={onContinue}>
          Continue to Sign →
        </Button>
      </div>
    </div>
  );
}

function HistoricSummary({
  amount,
  tokens,
  ticker,
  onContinue,
}: {
  amount: number;
  tokens: number;
  ticker: string;
  onContinue: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-card border border-border bg-card p-4 space-y-1 text-[13px]">
        <p className="font-semibold text-ink">Allocation issued</p>
        <p className="text-foreground-mute">
          <Money value={amount} /> · {tokens.toLocaleString()} {ticker}
        </p>
        <p className="text-[11px] text-foreground-mute">
          This subscription is past the allocation step.
        </p>
      </div>
      <div className="flex justify-end">
        <Button variant="primary" size="lg" onClick={onContinue}>
          Continue to Sign →
        </Button>
      </div>
    </div>
  );
}

function formatMonthYear(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function formatMonthDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
}
